import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import DateColumn from '@/components/date-column/DateColumn'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { spesa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { TipologiaSpesa } from '@/routes/expenses/ExpenseEditor.types'
import { getMainDocumentPreview } from '@/routes/expenses/utils'
import { HourEditorFormExpensesGridProps } from '@/routes/hours/HourEditor.types'
import { useScreenSize } from '@/themes/media-query'
import { Button } from 'devextreme-react'
import {
  Column,
  DataGrid,
  DataGridRef,
  Editing,
  Item,
  Paging,
  Toolbar,
  Button as GridButton,
  StateStoring,
  Summary,
  TotalItem,
  DataGridTypes,
} from 'devextreme-react/data-grid'
import DataSource from 'devextreme/data/data_source'
import ODataStore from 'devextreme/data/odata/store'
import { ClickEvent } from 'devextreme/ui/button'
import { ColumnButtonClickEvent, RowDblClickEvent } from 'devextreme/ui/data_grid'
import notify from 'devextreme/ui/notify'
import { memo, useRef } from 'react'

const HourEditorFormExpensesGrid = (props: HourEditorFormExpensesGridProps) => {
  const { impiegato, data, readOnly } = props
  // console.log('stampo impiegato', impiegato)
  const currentScreenSize = useScreenSize()
  const token = useTokenRefresh()
  const gridRef = useRef<DataGridRef>(null)

  const hourDate: Date = new Date(data ?? '')
  hourDate.setHours(0)
  hourDate.setMinutes(0)
  hourDate.setSeconds(0)
  hourDate.setMilliseconds(0)
  // console.log('hourDate', hourDate)
  const startDateFilter: Date = new Date(hourDate.toISOString().replace('Z', ''))
  const endDateFilter: Date = new Date(
    new Date(hourDate.getFullYear(), hourDate.getMonth(), hourDate.getDate() + 1, 0, 0, 0)
      .toISOString()
      .replace('Z', ''),
  )

  const dailyExpensesDataSource = new DataSource({
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/spesa`,
      key: 'id',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    filter: [
      ['impiegato.id', typeof impiegato === 'number' ? impiegato : impiegato?.id],
      ['data', '>=', startDateFilter],
      ['data', '<', endDateFilter],
    ],
    expand: ['tipologia', 'impiegato'],
  })

  const calculateSpesa = (options: DataGridTypes.CustomSummaryInfo) => {
    if (options.summaryProcess === 'start') {
      options.totalValue = options.name === 'Spesa' ? { vitto: 0, trasferta: 0, alloggio: 0 } : 0
    }

    if (options.name === 'Spesa' && options.summaryProcess === 'calculate') {
      const row: spesa = options.value
      switch (row.tipologia?.id) {
        case TipologiaSpesa.VITTO:
          options.totalValue.vitto += row.totale
          break
        case TipologiaSpesa.ALLOGGIO:
          options.totalValue.alloggio += row.totale
          break
        case TipologiaSpesa.TRASFERTA:
          options.totalValue.trasferta += row.totale
          break
        default:
          break
      }
    }
  }

  return (
    <>
      <DataGrid
        id={`daily-expenses-grid`}
        dataSource={dailyExpensesDataSource}
        noDataText="Nessuna spesa inserita nella data indicata"
        ref={gridRef}
        keyExpr={'id'}
        showBorders={true}
        showColumnLines={true}
        focusedRowEnabled={true}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnReordering={true}
        allowColumnResizing={true}
        rowAlternationEnabled={true}
        wordWrapEnabled={false}
        repaintChangesOnly={true}
        width="100%"
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') window.open(`/workactivities/expenses/${e.data.id}`, '_blank')
        }}
      >
        <StateStoring enabled={true} type="localStorage" storageKey={'daily-expenses-grid'} savingTimeout={50} />
        <Paging defaultPageSize={8} />
        <Editing mode="batch" allowAdding={false} allowDeleting={!readOnly} allowUpdating={false} />
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item location="after">
            <Button
              visible={!readOnly}
              icon="add"
              hint="Nuova spesa"
              onClick={(e: ClickEvent) => {
                window.open(
                  `/workactivities/expenses/new?date=${hourDate.toISOString()}&employeeId=${typeof impiegato === 'number' ? impiegato : impiegato?.id}`,
                  '_blank',
                )
              }}
            />
          </Item>
          <Item name="saveButton" />
          <Item name="revertButton" />
        </Toolbar>
        <Column
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="left"
          visible={true}
        >
          <GridButton
            hint="Anteprima"
            icon="search"
            onClick={async (e: ColumnButtonClickEvent) => {
              gridRef.current?.instance().beginCustomLoading('Anteprima scontrino')
              try {
                const data: spesa = e.row?.data
                await getMainDocumentPreview(data)
              } catch (error: any) {
                notify(`Errore anteprima scontrino. Dettagli: ${error}`, 'error', 2000)
              } finally {
                gridRef.current?.instance().endCustomLoading()
              }
            }}
          ></GridButton>
        </Column>
        <Column
          dataField={'id'}
          caption="ID"
          visible={false}
          dataType="number"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
        ></Column>
        <Column
          dataField={'ded_Dis'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: spesa = cellData.row.data
            return <ObjectCodeLink url={`/workactivities/expenses/${data.id}`} text={data.ded_Dis}></ObjectCodeLink>
          }}
          width={'auto'}
          caption="CODICE"
          visible={true}
          allowSearch={false}
          dataType="string"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
          }
        ></Column>
        {DateColumn({
          dataField: 'data',
          caption: 'DATA',
          width: 'auto',
          format: 'dd/MM/yyyy',
          defaultSortOrder: 'desc',
          hidingPriority:
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined,
        })}
        <Column
          dataField={'tipologia.nome'}
          caption="TIPOLOGIA"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
          }
        ></Column>
        <Column
          dataField={'note'}
          caption="NOTE"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
        ></Column>
        <Column
          dataField={'totale'}
          caption="TOTALE"
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
        ></Column>
        <Column
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="right"
          visible={true}
        >
          <GridButton name="delete"></GridButton>
        </Column>
        <Summary calculateCustomSummary={calculateSpesa}>
          <TotalItem
            name="Spesa"
            summaryType="custom"
            customizeText={(itemInfo: any) => {
              const value: { vitto: number; alloggio: number; trasferta: number } = itemInfo.value
              return `VITTO € ${value.vitto.toLocaleString('en-us', { minimumFractionDigits: 2 })} - ALLOGGIO € ${value.alloggio.toLocaleString('en-us', { minimumFractionDigits: 2 })} - TRASFERTA € ${value.trasferta.toLocaleString('en-us', { minimumFractionDigits: 2 })}`
            }}
            showInColumn="totale"
          />
          {/* <TotalItem column="totale" summaryType="sum" displayFormat="€ {0}" /> */}
        </Summary>
      </DataGrid>
    </>
  )
}

const HourEditorFormExpensesGridMemoed = memo(HourEditorFormExpensesGrid, (oldProps, newProps) => {
  return typeof newProps.impiegato === 'number' && typeof oldProps.impiegato === 'number'
    ? newProps.impiegato === oldProps.impiegato && oldProps.data === newProps.data
    : typeof oldProps.impiegato === 'object' &&
        typeof newProps.impiegato === 'object' &&
        oldProps.impiegato?.id === newProps.impiegato?.id &&
        oldProps.data === newProps.data
})

export default HourEditorFormExpensesGridMemoed
